// App.tsx
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "./Redux/store";
import MainPage from "./pages/MainPage";
import HealthCheck from "./pages/HealthCheck";
import ErrorBoundary from "./components/ErrorBoundary";
import CookieConsent from "./components/CookieConsent/CookieConsent";
import AuthService from "./services/api/AuthService";
import ConfigService, { AppConfig } from "./services/ConfigService";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import "./css/style.css";

function App() {
  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
  const [config, setConfig] = useState<AppConfig | null>(null);

  // Load configuration on app startup
  useEffect(() => {
    ConfigService.loadConfig()
      .then((loadedConfig) => {
        setConfig(loadedConfig);
      })
      .catch((error) => {
        console.error("Failed to load configuration:", error);
      });
  }, []);

  // Update document title and meta description when config is loaded
  useEffect(() => {
    if (config?.branding) {
      // Update page title
      document.title = config.branding.title;

      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', config.branding.description);
      } else {
        // Create meta description if it doesn't exist
        const meta = document.createElement('meta');
        meta.name = 'description';
        meta.content = config.branding.description;
        document.head.appendChild(meta);
      }

      console.log('✅ Page branding updated:', {
        title: config.branding.title,
        description: config.branding.description,
        name: config.branding.name
      });
    }
  }, [config]);

  // Applica la palette di brand del tenant (CSS variables) da config.json -> theme.
  // Se il tenant non definisce theme, restano i default verdi di :root (index.css).
  useEffect(() => {
    const primary = config?.theme?.primaryColor;
    if (!primary) return;
    const hover = config?.theme?.primaryColorHover || primary;
    const root = document.documentElement;
    const hexToRgb = (hex: string): string | null => {
      const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
      return m
        ? `${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}`
        : null;
    };
    root.style.setProperty("--primary-color", primary);
    root.style.setProperty("--primary-hover", hover);
    root.style.setProperty("--primary-accent", primary);
    root.style.setProperty("--primary-accent2", hover);
    root.style.setProperty("--primary-accent3", hover);
    root.style.setProperty("--primary-dark", hover);
    const rgb = hexToRgb(primary);
    if (rgb) root.style.setProperty("--primary-rgb", rgb);
  }, [config]);

  useEffect(() => {
    // Il proxy gestisce automaticamente i token client
    // Non è più necessario inizializzare token nel frontend
    console.log("🔐 AuthService ready - using proxy for authentication");
  }, []);

  return (
    <div className="app-container">
      <Router>
        {/* TOP BAR - Mostra solo quando l'utente NON è loggato */}
        {!isLoggedIn && config && (
          <div className="top-bar">
            <div className="container-fluid">
              <ul className="pull-right">
                <li>{config.address1?.label || "Sede:"}</li>
                <li>
                  <a href={config.address1?.url || "#"}>
                    <i className="fa fa-map-marker"></i> {config.address1?.address}
                  </a>
                </li>
                <li>
                  <a href={`tel:${config.address1?.telephone}`}>
                    <i className="fa fa-phone"></i> {config.address1?.telephone}
                  </a>
                </li>
                {config.address2?.show && (
                  <>
                    <li> | </li>
                    <li>{config.address2?.label}</li>
                    <li>
                      <a href={config.address2?.url || "#"}>
                        <i className="fa fa-map-marker"></i> {config.address2?.address}
                      </a>
                    </li>
                    {config.address2?.showTelephone && (
                      <li>
                        <a href={`tel:${config.address2?.telephone}`}>
                          <i className="fa fa-phone"></i> {config.address2?.telephone}
                        </a>
                      </li>
                    )}
                  </>
                )}
                {config.social?.showFacebook && (
                  <>
                    <li> | </li>
                    <li>
                      <a
                        href={config.social?.facebookUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <i className="pi pi-facebook"></i> {config.social?.facebookText}
                      </a>
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>
        )}

        {/* HEADER Principale - sempre visibile */}
        <header className="navbar">
          <div className="container">
            <div className="row">
              {/* Logo - sempre visibile, centrato quando utente è loggato */}
              <div className={isLoggedIn ? "col-md-12" : "col-md-5"}>
                <div
                  className="logo"
                  style={isLoggedIn ? { textAlign: "center" } : {}}
                >
                  {/* Renderizza il logo solo dopo il caricamento di config.json:
                      evita il flash di un logo di fallback errato al primo render */}
                  {config?.logo?.url && (
                    <a href={config.logo.mainSiteUrl || "#"}>
                      <img
                        className="img-responsive"
                        src={config.logo.url}
                        alt={config.logo.alt || "Logo"}
                        width={config.logo.width}
                        height={config.logo.height}
                        style={
                          isLoggedIn
                            ? { margin: "0 auto", display: "inline-block" }
                            : {}
                        }
                      />
                    </a>
                  )}
                </div>
              </div>
              {/* Titolo - mostra solo quando utente NON è loggato */}
              {!isLoggedIn && (
                <div className="col-md-7" style={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%" }}>
                  <h2
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      color: config?.theme?.primaryColor || "var(--primary-color, #229a61)",
                      fontSize: "30px",
                      marginBottom: config?.branding?.headerSubtitle ? "5px" : "0",
                    }}
                  >
                    {config?.branding?.headerTitle || "Referti on-line"}
                  </h2>
                  {config?.branding?.headerSubtitle && (
                    <p
                      style={{
                        textAlign: "center",
                        color: "#666",
                        fontSize: "14px",
                        margin: 0,
                      }}
                    >
                      {config.branding.headerSubtitle}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="main-content">
          <ErrorBoundary>
            <Routes>
              <Route path="/" Component={MainPage} />
              <Route path="/health" Component={HealthCheck} />
              <Route path="/api/health" Component={HealthCheck} />
            </Routes>
          </ErrorBoundary>
        </main>
      </Router>
      <footer className="app-footer">
        <div className="container">
          <div className="row margin-t-20">
            <div className="col-md-12">
              {config?.footer?.line1 && <p>{config.footer.line1}</p>}
              <p>
                {config?.footer?.line2 || "Powered by"}{" "}
                <a href={config?.footer?.line2LinkUrl || "https://www.dharmahealthcare.net"}>
                  {config?.footer?.line2LinkText || "Dharma Healthcare"}
                </a>
              </p>
            </div>
          </div>
          {config?.footer?.line3 && (
            <div className="row">
              <div className="col-md-12">
                <p>{config.footer.line3}</p>
              </div>
            </div>
          )}
        </div>
      </footer>
      <CookieConsent />
    </div>
  );
}

export default App;
